/**
 * The hand-off from the full-page onboarding assistant to the docked one. The
 * conversation is already shared (one session key); what crosses over is the
 * panel being open, and one line for screen readers saying the page changed.
 * Both live in storage so they survive the route change between the two.
 */

/** Open/closed state of the docked panel. */
export const ASSISTANT_PANEL_OPEN_KEY = 'revvly.agent.widget:open';
/** A one-shot announcement for the panel's live region. */
const DOCK_ANNOUNCEMENT_KEY = 'revvly.agent.widget:announce';

/** Ask the docked panel to open, and to announce the move once it does. */
export function requestDock(announcement: string): void {
  try {
    localStorage.setItem(ASSISTANT_PANEL_OPEN_KEY, 'true');
    sessionStorage.setItem(DOCK_ANNOUNCEMENT_KEY, announcement);
  } catch {
    // Storage may be unavailable (privacy mode): the panel then opens closed.
  }
}

/** The pending announcement, removed as it is read. */
export function takeDockAnnouncement(): string | null {
  try {
    const value = sessionStorage.getItem(DOCK_ANNOUNCEMENT_KEY);
    if (value) sessionStorage.removeItem(DOCK_ANNOUNCEMENT_KEY);
    return value;
  } catch {
    return null;
  }
}

/** The event a page sends to open the docked panel from a button of its own. */
export const OPEN_ASSISTANT_EVENT = 'revvly:assistant-open';

export interface OpenAssistantDetail {
  /**
   * Text to put in the composer. Only placed in the box: the person still sends it, so a button
   * cannot make the assistant do anything on its own.
   */
  draft?: string;
}

/** Open the docked assistant panel (the bubble listens for this), optionally with a draft. */
export function openAssistant(detail: OpenAssistantDetail = {}): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent<OpenAssistantDetail>(OPEN_ASSISTANT_EVENT, { detail }));
}
